import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Pencil, Trash2, BookOpen } from "lucide-react";
import { RequireAdmin } from "@/components/shared/require-admin";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { DeleteConfirmDialog } from "@/components/shared/delete-confirm-dialog";
import { FormError } from "@/components/shared/form-error";
import {
  useOkrExamples,
  useCreateOkrExample,
  useUpdateOkrExample,
  useDeleteOkrExample,
  type OkrExample,
} from "@/hooks/use-okr-examples";

// ── Zod Schema ──────────────────────────────────────────────

const okrExampleFormSchema = z.object({
  title: z.string().min(1, "Title is required").max(255),
  level: z.enum(["any", "department", "team", "individual"]),
  okr_type: z.enum(["any", "committed", "aspirational"]),
  coach_kind: z.enum(["both", "objective", "kr"]),
  strong_draft: z.string().min(1, "Strong draft is required").max(2000),
  weak_draft: z.string().max(2000).optional(),
  explanation: z.string().min(1, "Explanation is required").max(2000),
  sort_order: z.number().int().min(0).optional(),
});
type OkrExampleFormValues = z.infer<typeof okrExampleFormSchema>;

function getDefaults(example?: OkrExample): OkrExampleFormValues {
  return {
    title: example?.title ?? "",
    level: (example?.level as OkrExampleFormValues["level"]) ?? "any",
    okr_type: (example?.okr_type as OkrExampleFormValues["okr_type"]) ?? "any",
    coach_kind: (example?.coach_kind as OkrExampleFormValues["coach_kind"]) ?? "both",
    strong_draft: example?.strong_draft ?? "",
    weak_draft: example?.weak_draft ?? "",
    explanation: example?.explanation ?? "",
    sort_order: example?.sort_order ?? 0,
  };
}

// ── Label helpers ──────────────────────────────────────────

const LEVEL_LABELS: Record<string, string> = {
  any: "Any level",
  department: "Department",
  team: "Team",
  individual: "Individual",
};

const TYPE_LABELS: Record<string, string> = {
  any: "Any type",
  committed: "Committed",
  aspirational: "Aspirational",
};

const KIND_LABELS: Record<string, string> = {
  both: "Objective & KR",
  objective: "Objective only",
  kr: "KR only",
};

// ── Page export ─────────────────────────────────────────────

export function AdminOkrExamplesPage() {
  return (
    <RequireAdmin>
      <AdminOkrExamplesContent />
    </RequireAdmin>
  );
}

// ── Main content ─────────────────────────────────────────────

function AdminOkrExamplesContent() {
  const { data, isLoading, error } = useOkrExamples();
  const deleteMutation = useDeleteOkrExample();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<OkrExample | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OkrExample | null>(null);

  const examples = data?.data ?? [];

  function openCreate() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(example: OkrExample) {
    setEditing(example);
    setDialogOpen(true);
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h1 className="font-heading text-3xl font-semibold">OKR Examples Library</h1>
        <p className="text-sm text-destructive">
          Failed to load examples: {error instanceof Error ? error.message : "Unknown error"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <BookOpen className="h-6 w-6 text-[hsl(var(--primary))]" />
          <div>
            <h1 className="font-heading text-3xl font-semibold">OKR Examples Library</h1>
            <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
              Strong/weak example pairs used by the AI OKR Coach to illustrate best practices.
            </p>
          </div>
        </div>
        <Button onClick={openCreate} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Example
        </Button>
      </div>

      {examples.length === 0 ? (
        <EmptyState
          title="No examples yet"
          description="Add strong/weak OKR example pairs to help the coach illustrate best practices."
          actionLabel="Add Example"
          onAction={openCreate}
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead className="w-32">Level</TableHead>
              <TableHead className="w-32">Type</TableHead>
              <TableHead className="w-36">Used for</TableHead>
              <TableHead className="w-20 text-right">Order</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {examples.map((example) => (
              <TableRow key={example.id}>
                <TableCell className="font-medium">{example.title}</TableCell>
                <TableCell>
                  <Badge variant="outline">{LEVEL_LABELS[example.level] ?? example.level}</Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{TYPE_LABELS[example.okr_type] ?? example.okr_type}</Badge>
                </TableCell>
                <TableCell className="text-sm text-[hsl(var(--muted-foreground))]">
                  {KIND_LABELS[example.coach_kind] ?? example.coach_kind}
                </TableCell>
                <TableCell className="text-right text-sm text-[hsl(var(--muted-foreground))]">
                  {example.sort_order}
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openEdit(example)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(example)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <OkrExampleDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
      />

      <DeleteConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete OKR Example"
        description={`Delete "${deleteTarget?.title}"? This cannot be undone.`}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
          setDeleteTarget(null);
        }}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}

// ── Create / Edit dialog ──────────────────────────────────────

interface OkrExampleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: OkrExample | null;
}

function OkrExampleDialog({ open, onOpenChange, editing }: OkrExampleDialogProps) {
  const createMutation = useCreateOkrExample();
  const updateMutation = useUpdateOkrExample(editing?.id ?? "");

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<OkrExampleFormValues>({
    resolver: zodResolver(okrExampleFormSchema),
    defaultValues: getDefaults(editing ?? undefined),
  });

  // Reset form when dialog opens/editing changes
  const handleOpenChange = (open: boolean) => {
    if (open) reset(getDefaults(editing ?? undefined));
    onOpenChange(open);
  };

  const onSubmit = (values: OkrExampleFormValues) => {
    const payload = {
      title: values.title,
      level: values.level,
      okr_type: values.okr_type,
      coach_kind: values.coach_kind,
      strong_draft: values.strong_draft,
      weak_draft: values.weak_draft || undefined,
      explanation: values.explanation,
      sort_order: values.sort_order ?? 0,
    };

    if (editing) {
      updateMutation.mutate(payload, { onSuccess: () => onOpenChange(false) });
    } else {
      createMutation.mutate(payload, { onSuccess: () => onOpenChange(false) });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit OKR Example" : "Add OKR Example"}</DialogTitle>
          <DialogDescription>
            Provide a strong draft, an optional weak draft, and an explanation of the difference.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="ex-title">Title</Label>
            <Input id="ex-title" placeholder="e.g., Strong Committed Team KR" {...register("title")} />
            <FormError message={errors.title?.message} />
          </div>

          {/* Filters row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Level</Label>
              <Select
                value={watch("level")}
                onValueChange={(v) => setValue("level", v as OkrExampleFormValues["level"])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any level</SelectItem>
                  <SelectItem value="department">Department</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>OKR Type</Label>
              <Select
                value={watch("okr_type")}
                onValueChange={(v) => setValue("okr_type", v as OkrExampleFormValues["okr_type"])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any type</SelectItem>
                  <SelectItem value="committed">Committed</SelectItem>
                  <SelectItem value="aspirational">Aspirational</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Used for</Label>
              <Select
                value={watch("coach_kind")}
                onValueChange={(v) => setValue("coach_kind", v as OkrExampleFormValues["coach_kind"])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Objective &amp; KR</SelectItem>
                  <SelectItem value="objective">Objective only</SelectItem>
                  <SelectItem value="kr">KR only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Strong draft */}
          <div className="space-y-1.5">
            <Label htmlFor="ex-strong">Strong draft</Label>
            <Textarea
              id="ex-strong"
              rows={4}
              placeholder="The well-written version of the OKR..."
              {...register("strong_draft")}
            />
            <FormError message={errors.strong_draft?.message} />
          </div>

          {/* Weak draft */}
          <div className="space-y-1.5">
            <Label htmlFor="ex-weak">
              Weak draft{" "}
              <span className="text-xs font-normal text-[hsl(var(--muted-foreground))]">(optional)</span>
            </Label>
            <Textarea
              id="ex-weak"
              rows={3}
              placeholder="The common but flawed version to contrast against..."
              {...register("weak_draft")}
            />
            <FormError message={errors.weak_draft?.message} />
          </div>

          {/* Explanation */}
          <div className="space-y-1.5">
            <Label htmlFor="ex-explain">Explanation</Label>
            <Textarea
              id="ex-explain"
              rows={3}
              placeholder="What makes the strong draft work, and why the weak draft falls short..."
              {...register("explanation")}
            />
            <FormError message={errors.explanation?.message} />
          </div>

          {/* Sort order */}
          <div className="w-32 space-y-1.5">
            <Label htmlFor="ex-sort">Sort order</Label>
            <Input
              id="ex-sort"
              type="number"
              min={0}
              {...register("sort_order", { valueAsNumber: true })}
            />
            <FormError message={errors.sort_order?.message} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Saving..." : editing ? "Save Changes" : "Add Example"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
